import type { InvoiceChannel } from "@/lib/api/types";

/**
 * The inline, non-blocking nudge on the covers row (covers rework, Phase 2c)
 * — never a refusal. A genuinely huge party is a real bill, the till cannot
 * verify a headcount, and the common slip this exists to catch is a waiter
 * typing the TABLE NUMBER into the covers field by habit, not a party that
 * really is twice the table's capacity.
 *
 * Silent unless every one of the bill's own tables carries a recorded seat
 * count: one missing `seats` makes "twice the table's capacity" unknowable
 * rather than merely approximate, and a hint that fires on a guess would
 * train staff to dismiss it. `tableSeats` carries one entry per table on the
 * bill — `null` for a table whose seats are not recorded, or one the caller
 * could not resolve at all (an unauthorised read counts the same as an
 * unrecorded value: the number is not knowable either way).
 */
export function shouldShowCoversSanityHint(input: {
  channel: InvoiceChannel;
  guestCount: number | null;
  tableSeats: (number | null)[];
}): boolean {
  if (input.channel !== "DINE_IN") return false;
  if (input.guestCount === null) return false;
  if (input.tableSeats.length === 0) return false;
  if (input.tableSeats.some((seats) => seats === null)) return false;
  const totalSeats = input.tableSeats.reduce<number>(
    (sum, seats) => sum + (seats ?? 0),
    0,
  );
  return input.guestCount > 2 * totalSeats;
}
